import { useEffect, useRef, useState } from 'react';

interface Suggestion {
  display_name: string;
  city: string;
  country: string;
}

interface Props {
  value: string;
  onChange: (city: string) => void;
  placeholder?: string;
  id?: string;
}

const PROXY = `${import.meta.env.VITE_SUPABASE_URL ?? ''}/functions/v1/nominatim-proxy`;

export function CityAutocomplete({ value, onChange, placeholder, id }: Props) {
  const [query, setQuery]           = useState(value);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen]             = useState(false);
  const [loading, setLoading]       = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleInput(q: string) {
    setQuery(q);
    onChange(q);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.length < 2) { setSuggestions([]); setOpen(false); return; }

    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const url = `${PROXY}?mode=search&q=${encodeURIComponent(q)}&limit=6&featuretype=city`;
        const res  = await fetch(url);
        if (!res.ok) throw new Error('proxy error');
        const data = await res.json() as Array<{
          display_name: string;
          address?: { city?: string; town?: string; village?: string; country?: string };
        }>;
        const items = data.slice(0, 6).map(r => ({
          display_name: r.display_name,
          city: r.address?.city ?? r.address?.town ?? r.address?.village ?? r.display_name.split(',')[0],
          country: r.address?.country ?? '',
        }));
        setSuggestions(items);
        setOpen(items.length > 0);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 320);
  }

  function select(s: Suggestion) {
    const label = s.country ? `${s.city}, ${s.country}` : s.city;
    setQuery(label);
    onChange(label);
    setSuggestions([]);
    setOpen(false);
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        id={id}
        type="text"
        className="form-input"
        value={query}
        onChange={e => handleInput(e.target.value)}
        placeholder={placeholder ?? 'Ex: São Paulo, Brasil'}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-haspopup="listbox"
      />
      {loading && (
        <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>...</span>
      )}
      {open && suggestions.length > 0 && (
        <ul
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 50,
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-modal)',
            margin: '4px 0 0',
            padding: '0.25rem',
            listStyle: 'none',
            maxHeight: '220px',
            overflowY: 'auto',
          }}
        >
          {suggestions.map((s, i) => (
            <li key={i}>
              <button
                role="option"
                type="button"
                onClick={() => select(s)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--text-body-sm)',
                  color: 'var(--text-primary)',
                  transition: 'background var(--transition-fast)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(31,78,95,0.07)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <strong>{s.city}</strong>
                {s.country && <span style={{ color: 'var(--text-muted)', marginLeft: '0.4rem' }}>{s.country}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
